import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Text,
  useDisclosure,
} from '@chakra-ui/react'
import {
  ArrowsRightLeftIcon,
  Button,
  ChevronLeftIcon,
  Dialog,
  LightModeBox,
  LoaderCover,
  QrCodeIcon,
} from 'components'
import { useCurrentUser } from 'components/providers'
import { isEmpty } from 'lodash'
import { Suspense, lazy, useState } from 'react'
import { PrimaryLeadProperties } from 'types'
import { updateDoc, useBreakpoint, useIntercomUtils, useScrolling } from 'utils'
import { useEvent } from '../../../utils/eventUtils'
import PropertyMapper from '../../PropertyMapper'

const QrScanner = lazy(() => import('./QrScanner'))

interface Props {
  isOpen: boolean
  onClose: () => void
}

export default function QrMappingDrawer({ isOpen, onClose }: Props) {
  useIntercomUtils(isOpen)
  const user = useCurrentUser()
  const { event } = useEvent()
  const [qrFields, setQrFields] = useState<Record<string, string>>()
  const [leadMapping, setLeadMapping] = useState<PrimaryLeadProperties>()
  const { isOpen: dialogIsOpen, onOpen: dialogOnOpen, onClose: dialogOnClose } = useDisclosure()
  const isMobile = useBreakpoint() < 2

  async function saveMapping() {
    if (!event || !leadMapping) return

    const qrCode = { ...event.qrCode!, leadMapping }
    await updateDoc(user!.id, 'events', { id: event.id!, qrCode })

    onClose()
  }

  const { scrolling, overflowRef, contentRef } = useScrolling()

  return (
    <>
      <Drawer
        placement="right"
        onClose={onClose}
        autoFocus={false}
        size={isMobile ? 'xs' : 'lg'}
        isOpen={isOpen}
        closeOnEsc
      >
        <DrawerOverlay />
        <LightModeBox>
          <DrawerContent borderRadius="md" my={2} mr={2}>
            <DrawerHeader borderBottom="1px solid rgba(222, 226, 244, 1)" py={2}>
              <Button
                variant="ghost"
                leftIcon={<ChevronLeftIcon fontSize="lg" />}
                onClick={onClose}
                pl={0}
              >
                Back
              </Button>

              <DrawerCloseButton h="40px" />
            </DrawerHeader>

            <DrawerBody ref={overflowRef}>
              {qrFields ? (
                <Flex ref={contentRef} direction="column" gap={3}>
                  <Flex justify="space-between" px={5}>
                    <Flex gap={2} align="center" p={2}>
                      <ArrowsRightLeftIcon fontSize="xl" />
                      <Text textStyle="h6">Map Fields</Text>
                    </Flex>
                    <Button variant="outline" leftIcon={<QrCodeIcon />} onClick={dialogOnOpen}>
                      Re-Scan Test QR Code
                    </Button>
                  </Flex>
                  <PropertyMapper
                    inputFieldLabel="XpressLeads Fields"
                    inputMap={qrFields}
                    onChange={setLeadMapping}
                  />
                </Flex>
              ) : (
                <Suspense fallback={<LoaderCover show={true} />}>
                  <QrScanner onRead={setQrFields} />
                </Suspense>
              )}
            </DrawerBody>

            <DrawerFooter borderTop={scrolling ? '1px' : 'none'} borderColor="gray.300">
              <Button palette="primary" isDisabled={isEmpty(leadMapping)} onClick={saveMapping}>
                Save
              </Button>
            </DrawerFooter>
          </DrawerContent>
        </LightModeBox>
      </Drawer>
      <Dialog
        isOpen={dialogIsOpen}
        onClose={dialogOnClose}
        size="2xl"
        isCentered
        bodyProps={{ display: 'flex', p: 6 }}
      >
        <QrScanner
          onRead={value => {
            setQrFields(value)
            dialogOnClose()
          }}
        />
      </Dialog>
    </>
  )
}
